import React, { useState } from 'react';
import {
  Church,
  Users,
  Calendar,
  TrendingUp,
  Activity,
  Award,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import type { Ministerio } from '../../services/ministries/ministries.service';

interface MinisteriosStatsProps {
  ministerios: Ministerio[];
  isLoading: boolean;
}

export const MinisteriosStats: React.FC<MinisteriosStatsProps> = ({ ministerios, isLoading }) => {
  const [tablePage, setTablePage] = useState(1);
  const tableLimit = 7;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-64"></div>
          ))}
        </div>
      </div>
    );
  }

  if (ministerios.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center mb-4">
          <BarChart3 className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-xs font-medium text-gray-900 mb-2">No hay datos para analizar</h3>
        <p className="text-gray-500">Crea ministerios para ver sus estadísticas.</p>
      </div>
    );
  }

  // Cálculos
  const totalMinisterios = ministerios.length;
  const activos = ministerios.filter(m => m.estado).length;
  const inactivos = totalMinisterios - activos;
  const totalIntegrantes = ministerios.reduce((sum, m) => sum + (m.integrantes?.length || 0), 0);
  const totalEventos = ministerios.reduce((sum, m) => sum + (m.eventos?.length || 0), 0);
  const promedioIntegrantes = totalMinisterios > 0 ? (totalIntegrantes / totalMinisterios).toFixed(1) : '0';

  // Top ministerios por integrantes
  const topPorIntegrantes = [...ministerios]
    .sort((a, b) => (b.integrantes?.length || 0) - (a.integrantes?.length || 0))
    .slice(0, 5);

  // Top ministerios por eventos
  const topPorEventos = [...ministerios]
    .sort((a, b) => (b.eventos?.length || 0) - (a.eventos?.length || 0))
    .slice(0, 5);

  // Ministerios sin líder
  const sinLider = ministerios.filter(m => !m.lider).length;
  const conLider = totalMinisterios - sinLider;

  // Distribución de estado para el gráfico de barras visual
  const activosPct = totalMinisterios > 0 ? Math.round((activos / totalMinisterios) * 100) : 0;
  const inactivosPct = totalMinisterios > 0 ? Math.round((inactivos / totalMinisterios) * 100) : 0;

  const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }: {
    title: string;
    value: string | number;
    subtitle?: string;
    icon: React.ElementType;
    color: 'purple' | 'green' | 'orange' | 'blue' | 'red';
    trend?: 'up' | 'down' | 'neutral';
  }) => {
    const colorMap = {
      purple: 'bg-purple-50 text-purple-600 border-purple-100',
      green: 'bg-green-50 text-green-600 border-green-100',
      orange: 'bg-orange-50 text-orange-600 border-orange-100',
      blue: 'bg-blue-50 text-blue-600 border-blue-100',
      red: 'bg-red-50 text-red-600 border-red-100',
    };
    const iconBgMap = {
      purple: 'bg-purple-100 text-purple-600',
      green: 'bg-green-100 text-green-600',
      orange: 'bg-orange-100 text-orange-600',
      blue: 'bg-blue-100 text-blue-600',
      red: 'bg-red-100 text-red-600',
    };

    return (
      <div className={`bg-white rounded-xl border p-6 transition-all hover:shadow-md ${colorMap[color]}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium opacity-80">{title}</p>
            <p className="text-xs font-medium mt-2 text-gray-900">{value}</p>
            {subtitle && <p className="text-xs mt-1 opacity-70">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${iconBgMap[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
        {trend && (
          <div className="mt-4 flex items-center text-xs">
            {trend === 'up' && <ArrowUpRight className="w-4 h-4 mr-1 text-green-600" />}
            {trend === 'down' && <ArrowDownRight className="w-4 h-4 mr-1 text-red-600" />}
            {trend === 'neutral' && <Minus className="w-4 h-4 mr-1 text-gray-500" />}
            <span className={trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-500'}>
              {trend === 'up' ? 'Activo' : trend === 'down' ? 'Inactivo' : 'Sin cambios'}
            </span>
          </div>
        )}
      </div>
    );
  };

  const ProgressBar = ({ label, value, max, color = 'purple' }: {
    label: string;
    value: number;
    max: number;
    color?: 'purple' | 'green' | 'orange' | 'blue';
  }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    const colorMap = {
      purple: 'bg-purple-600',
      green: 'bg-green-500',
      orange: 'bg-orange-500',
      blue: 'bg-blue-500',
    };

    return (
      <div className="mb-4 last:mb-0">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-medium text-gray-700 truncate pr-2">{label}</span>
          <span className="text-xs font-medium text-gray-900">{value}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all duration-500 ${colorMap[color]}`}
            style={{ width: `${pct}%` }}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tarjetas principales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Ministerios"
          value={totalMinisterios}
          subtitle={`${activos} activos · ${inactivos} inactivos`}
          icon={Church}
          color="purple"
          trend={activos > inactivos ? 'up' : 'down'}
        />
        <StatCard
          title="Integrantes Totales"
          value={totalIntegrantes}
          subtitle={`Promedio ${promedioIntegrantes} por ministerio`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Eventos Registrados"
          value={totalEventos}
          subtitle="En todos los ministerios"
          icon={Calendar}
          color="orange"
        />
        <StatCard
          title="Con Líder Asignado"
          value={conLider}
          subtitle={`${sinLider} sin líder`}
          icon={Award}
          color="green"
        />
      </div>

      {/* Gráficos y listas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distribución de estado */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="text-xs font-medium text-gray-900">Estado de Ministerios</h3>
          </div>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700">Activos</span>
                <span className="text-xs font-medium text-green-600">{activos} ({activosPct}%)</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
                <div
                  className="h-4 rounded-full bg-green-500 transition-all duration-700"
                  style={{ width: `${activosPct}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700">Inactivos</span>
                <span className="text-xs font-medium text-red-600">{inactivos} ({inactivosPct}%)</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
                <div
                  className="h-4 rounded-full bg-red-400 transition-all duration-700"
                  style={{ width: `${inactivosPct}%` }}
                ></div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-green-700">{activos}</p>
                  <p className="text-xs text-green-600 font-medium mt-1">Activos</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-xs font-medium text-red-700">{inactivos}</p>
                  <p className="text-xs text-red-600 font-medium mt-1">Inactivos</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Liderazgo */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Award className="w-5 h-5 text-purple-600" />
            <h3 className="text-xs font-medium text-gray-900">Liderazgo</h3>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-700">{conLider}</p>
                <p className="text-xs text-blue-600 font-medium mt-1">Con líder</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-xs font-medium text-gray-700">{sinLider}</p>
                <p className="text-xs text-gray-600 font-medium mt-1">Sin líder</p>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-700">Cobertura de liderazgo</span>
                <span className="text-xs font-medium text-blue-600">
                  {totalMinisterios > 0 ? Math.round((conLider / totalMinisterios) * 100) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden">
                <div
                  className="h-4 rounded-full bg-blue-500 transition-all duration-700"
                  style={{ width: `${totalMinisterios > 0 ? (conLider / totalMinisterios) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top ministerios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top por integrantes */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Users className="w-5 h-5 text-purple-600" />
            <h3 className="text-xs font-medium text-gray-900">Top Ministerios por Integrantes</h3>
          </div>
          <div>
            {topPorIntegrantes.length > 0 ? (
              topPorIntegrantes.map((m, idx) => (
                <ProgressBar
                  key={m.id}
                  label={`${idx + 1}. ${m.nombre}`}
                  value={m.integrantes?.length || 0}
                  max={topPorIntegrantes[0].integrantes?.length || 1}
                  color={idx === 0 ? 'green' : idx === 1 ? 'blue' : 'purple'}
                />
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">No hay datos de integrantes</p>
            )}
          </div>
        </div>

        {/* Top por eventos */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Calendar className="w-5 h-5 text-purple-600" />
            <h3 className="text-xs font-medium text-gray-900">Top Ministerios por Eventos</h3>
          </div>
          <div>
            {topPorEventos.length > 0 ? (
              topPorEventos.map((m, idx) => (
                <ProgressBar
                  key={m.id}
                  label={`${idx + 1}. ${m.nombre}`}
                  value={m.eventos?.length || 0}
                  max={topPorEventos[0].eventos?.length || 1}
                  color={idx === 0 ? 'orange' : idx === 1 ? 'purple' : 'blue'}
                />
              ))
            ) : (
              <p className="text-xs text-gray-500 text-center py-4">No hay eventos registrados</p>
            )}
          </div>
        </div>
      </div>

      {/* Tabla resumen con paginación */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            <h3 className="text-xs font-medium text-gray-900">Resumen por Ministerio</h3>
          </div>
          <span className="text-xs text-gray-500">
            Mostrando <span className="font-medium text-gray-900">{Math.min((tablePage - 1) * tableLimit + 1, ministerios.length)}–{Math.min(tablePage * tableLimit, ministerios.length)}</span> de <span className="font-medium text-gray-900">{ministerios.length}</span>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Ministerio</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Estado</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Integrantes</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Eventos</th>
                <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Líder</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ministerios
                .slice((tablePage - 1) * tableLimit, tablePage * tableLimit)
                .map((m) => (
                <tr key={m.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-xs font-medium text-gray-900">{m.nombre}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      m.estado ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {m.estado ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-700 text-center">{m.integrantes?.length || 0}</td>
                  <td className="px-6 py-4 text-xs text-gray-700 text-center">{m.eventos?.length || 0}</td>
                  <td className="px-6 py-4 text-xs text-gray-700">
                    {m.lider ? `${m.lider.nombres} ${m.lider.apellidos}` : (
                      <span className="text-gray-400 italic">Sin líder</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {ministerios.length > tableLimit && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/30 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Página <span className="font-medium text-gray-900">{tablePage}</span> de <span className="font-medium text-gray-900">{Math.ceil(ministerios.length / tableLimit)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setTablePage(p => Math.max(1, p - 1))}
                disabled={tablePage <= 1}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                  tablePage > 1
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setTablePage(p => Math.min(Math.ceil(ministerios.length / tableLimit), p + 1))}
                disabled={tablePage >= Math.ceil(ministerios.length / tableLimit)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                  tablePage < Math.ceil(ministerios.length / tableLimit)
                    ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Siguiente"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
